import { Button } from '@/components/commons';
import { AyumiService } from '@/services/ayumi';
import { IAyumiCategoryResponse } from '@/types/response';
import { NOTIFY_STATUS, notificationMessage } from '@/utils';
import { PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { CategoryCard } from './CategoryCard';

interface IProps {
  topicId: number;
  categories: IAyumiCategoryResponse[];
  offerOptions: { value: number; label: string }[];
  onChanged: () => void;
  /** カード単位の未保存状態を集約してアクションカードの警告に使う */
  onDirtyChange: (key: string, isDirty: boolean) => void;
}

/**
 * 「そなえる」タブ。カテゴリカードは1枚ずつ独立して保存する（Figma のカードごとの「保存」ボタン）。
 * 並び替えは Figma ではドラッグハンドルだが、admin に DnD ライブラリが無いため上下ボタンで代替している。
 */
export const PrepareTab: React.FC<IProps> = ({
  topicId,
  categories,
  offerOptions,
  onChanged,
  onDirtyChange,
}) => {
  const [isAdding, setIsAdding] = useState(false);

  const { mutate: reorder } = useMutation(
    (categoryIds: number[]) =>
      AyumiService.reorderCategories({ categoryIds }, topicId),
    {
      onSuccess: onChanged,
      onError: () => {
        notificationMessage({
          message: '並び替えに失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
        onChanged();
      },
    },
  );

  const handleMove = (index: number, direction: -1 | 1) => {
    const next = [...categories];
    const target = index + direction;

    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target], next[index]];
    reorder(next.map((category) => category.id));
  };

  return (
    <div className='flex flex-col gap-4'>
      {categories.length === 0 && !isAdding && (
        <p className='text-[13px] text-[#888]'>
          カテゴリがまだありません。「カテゴリを追加」から作成してください。
        </p>
      )}

      {categories.map((category, index) => (
        <CategoryCard
          key={category.id}
          topicId={topicId}
          category={category}
          offerOptions={offerOptions}
          canMoveUp={index > 0}
          canMoveDown={index < categories.length - 1}
          onMove={(direction) => handleMove(index, direction)}
          onSaved={onChanged}
          onDirtyChange={(isDirty) =>
            onDirtyChange(String(category.id), isDirty)
          }
        />
      ))}

      {isAdding && (
        <CategoryCard
          topicId={topicId}
          offerOptions={offerOptions}
          canMoveUp={false}
          canMoveDown={false}
          onMove={() => undefined}
          onSaved={() => {
            setIsAdding(false);
            onChanged();
          }}
          onCancelNew={() => setIsAdding(false)}
          onDirtyChange={(isDirty) => onDirtyChange('new', isDirty)}
        />
      )}

      {!isAdding && (
        <Button icon={<PlusOutlined />} onClick={() => setIsAdding(true)}>
          カテゴリを追加
        </Button>
      )}
    </div>
  );
};
